import { useState } from 'react';
import styled from 'styled-components';

import { localeMoment } from '@/utils/time';
import { ReactComponent as Checked } from '@/assets/images/order/checked.svg';
import { ReactComponent as Unchecked } from '@/assets/images/order/unchecked.svg';
import useOrderQuery from '@/hooks/order/useOrderQuery';
import useModal from '@/hooks/common/useModal';
import TextComponent from '@/components/TextComponent';
import GlobalAlert from '@/components/GlobalAlert';
import { Agreement } from '../OrderInfo';
import Tooltip from '@/components/Tooltip';
import PrintShippingLabelButton from '@/pages/OrderDetailPage/containers/PrintShippingLabelButton';
import { Order } from '@/apis/order/interface';
import { PRINT_SHIPPING_LABEL_BUTTON_TYPE } from '@/services/phamacy';
import OrderResetForm from '../OrderResetForm';
import theme from '@/styles/theme';
import useSetCompletePartnershipPickupOrder from '@/hooks/order/useSetCompletePartnershipPickupOrder';
import OrderReady from './ready/OrderReady';
import { OrderStatusType } from '@/@types/order';
import OrderWaitingPayment from './waitingPayment/OrderWaitingPayment';
import PartnershipPickup from './partnershipPickup/PartnershipPickup';
import ImmediateOrder from './immediateOrder/ImmediateOrder';

interface Props {
  order: Order;
  orderStatus: OrderStatusType;
}

const BottomButton = ({ order, orderStatus }: Props) => {
  const isUnavailablePickupComplete =
    order.orderType === 'dawn' &&
    localeMoment()
      .set({
        hour: 19,
        minute: 0,
        second: 0
      })
      .isBefore(localeMoment());

  const {
    orderCancelByUserMutation,
    orderPickupCallMutation,
    orderStartDeliveryMutation
  } = useOrderQuery();

  const { setCompletePartnershipPickupOrderMutate } =
    useSetCompletePartnershipPickupOrder();

  const [OrderResetModal, setOrderResetModal] = useModal(false);

  const [checkList, setCheckList] = useState<Agreement>({
    dur: false,
    useAlternative: undefined,
    disabledAlternative: false,
    guide: false,
    pickup: false
  });

  const onPressCheck = (policy: keyof Agreement, status?: boolean) => () => {
    setCheckList({
      ...checkList,
      [policy]: status !== undefined ? status : !checkList[policy]
    });
  };

  const onSubmitOrderCancelByUser = () => {
    if (!checkList.dur) {
      return GlobalAlert.show({
        title: 'DUR에서 해당 처방전을 삭제해주세요.'
      });
    }
    orderCancelByUserMutation.mutate(order.id);
  };

  const onSubmitPickupCall = () => {
    orderPickupCallMutation.mutate({
      orderId: order.orderId
    });
  };

  const onSubmitPickupComplete = () => {
    orderStartDeliveryMutation.mutate({
      orderId: String(order.id)
    });
  };

  const onSubmitPartnershipPickupComplete = () => {
    setCompletePartnershipPickupOrderMutate({
      request: {
        orderId: String(order.id)
      }
    });
  };

  // 접수 취소 요청
  const renderCancelByUser = () => (
    <>
      <RequestCancel>
        <Row>
          <TextComponent fontSize={16}>
            환자가 접수를 취소 하였습니다.
          </TextComponent>
        </Row>
        <Button
          background={theme.DF_COLOR.WHITE}
          borderColor={theme.DF_COLOR.GREEN500}
          disabled={!checkList.dur}
          onClick={() => onSubmitOrderCancelByUser()}>
          <ButtonText color={theme.DF_COLOR.GREEN500}>취소 확인</ButtonText>
        </Button>
      </RequestCancel>
      <CheckItem onClick={onPressCheck('dur')}>
        {checkList.dur ? <Checked /> : <Unchecked />}
        <CheckTextRow>
          <CheckItemText color={theme.DF_COLOR.PRIMARY500}>[필수]&nbsp;</CheckItemText>
          <CheckItemText color={theme.DF_COLOR.BLACK}>
            DUR에 처방전 삭제 확인
          </CheckItemText>
        </CheckTextRow>
      </CheckItem>
    </>
  );

  const renderRequestPickup = () => {
    switch (order.orderType) {
      case 'quick':
        // 퀵 배차 요청 (배차요청시간)
        return (
          <RequestPickup>
            <Button
              style={{ flex: 0.5 }}
              background={theme.DF_COLOR.WHITE}
              borderColor={theme.DF_COLOR.GRAY100}
              onClick={() => setOrderResetModal(true)}>
              <ButtonText color={theme.DF_COLOR.BLACK}>조제 접수 초기화</ButtonText>
            </Button>
            <Button
              style={{ flex: 0.5 }}
              background={theme.DF_COLOR.GREEN500}
              borderColor={theme.DF_COLOR.GREEN500}
              onClick={() => onSubmitPickupCall()}>
              <ButtonText color={theme.DF_COLOR.WHITE}>픽업 요청</ButtonText>
            </Button>
          </RequestPickup>
        );
      case 'delivery':
        switch (order.agencyType) {
          case 'doobal':
            return (
              <ReadyStatus>
                <Button
                  style={{ flex: 0.5 }}
                  background={theme.DF_COLOR.WHITE}
                  borderColor={theme.DF_COLOR.GRAY100}
                  onClick={() => setOrderResetModal(true)}>
                  <ButtonText color={theme.DF_COLOR.BLACK}>조제 접수 초기화</ButtonText>
                </Button>
                <PrintShippingLabelButton
                  type={PRINT_SHIPPING_LABEL_BUTTON_TYPE.DOOBAL_HERO_DELIVERY}
                  orderId={order.id}
                />
              </ReadyStatus>
            );
          default:
            // 퀵 배차 요청 (배차요청시간)
            return (
              <RequestPickup>
                <Button
                  style={{ flex: 0.5 }}
                  background={theme.DF_COLOR.WHITE}
                  borderColor={theme.DF_COLOR.GRAY100}
                  onClick={() => setOrderResetModal(true)}>
                  <ButtonText color={theme.DF_COLOR.BLACK}>조제 접수 초기화</ButtonText>
                </Button>
                <Button
                  style={{ flex: 0.5 }}
                  background={theme.DF_COLOR.GREEN500}
                  borderColor={theme.DF_COLOR.GREEN500}
                  onClick={() => onSubmitPickupCall()}>
                  <ButtonText color={theme.DF_COLOR.WHITE}>픽업 요청</ButtonText>
                </Button>
              </RequestPickup>
            );
        }
      case 'dawn':
        // 새벽 배송
        switch (order.agencyType) {
          case 'team_fresh':
            return (
              <ReadyStatus>
                <Button
                  style={{ flex: 0.5 }}
                  background={theme.DF_COLOR.WHITE}
                  borderColor={theme.DF_COLOR.GRAY100}
                  onClick={() => setOrderResetModal(true)}>
                  <ButtonText color={theme.DF_COLOR.BLACK}>조제 접수 초기화</ButtonText>
                </Button>
                <PrintShippingLabelButtonWrapper>
                  <PrintShippingLabelButton
                    type={PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN}
                    orderId={order.id}
                    orderNumber={order.orderId}
                  />
                </PrintShippingLabelButtonWrapper>
              </ReadyStatus>
            );
          default:
            return (
              <ReadyStatus>
                <Button
                  style={{ flex: 0.5 }}
                  background={theme.DF_COLOR.WHITE}
                  borderColor={theme.DF_COLOR.GRAY100}
                  onClick={() => setOrderResetModal(true)}>
                  <ButtonText color={theme.DF_COLOR.BLACK}>조제 접수 초기화</ButtonText>
                </Button>
                <PrintShippingLabelButtonWrapper>
                  <PrintShippingLabelButton
                    type={PRINT_SHIPPING_LABEL_BUTTON_TYPE.VROONG_DAWN}
                    orderId={order.id}
                  />
                </PrintShippingLabelButtonWrapper>
              </ReadyStatus>
            );
        }
      default:
        // 택배 배차 요청 (배차요청시간)
        return (
          <ReadyStatus>
            <Button
              style={{ flex: 0.5 }}
              background={theme.DF_COLOR.WHITE}
              borderColor={theme.DF_COLOR.GRAY100}
              onClick={() => setOrderResetModal(true)}>
              <ButtonText color={theme.DF_COLOR.BLACK}>조제 접수 초기화</ButtonText>
            </Button>
            <Button
              style={{ flex: 0.5 }}
              background={theme.DF_COLOR.GREEN500}
              borderColor={theme.DF_COLOR.GREEN500}
              onClick={() => onSubmitPickupCall()}>
              <ButtonText color={theme.DF_COLOR.WHITE}>약 배달 준비 완료</ButtonText>
            </Button>
          </ReadyStatus>
        );
    }
  };

  // 수거 승인
  const renderPickupCheck = () => {
    const ReprintButton = () => {
      switch (order.orderType) {
        case 'dawn':
          switch (order.agencyType) {
            case 'team_fresh':
              return (
                <PrintShippingLabelButton
                  type={
                    PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN_REPRINT
                  }
                  orderId={order.id}
                  orderNumber={order.orderId}
                />
              );
            default:
              return (
                <PrintShippingLabelButton
                  type={PRINT_SHIPPING_LABEL_BUTTON_TYPE.VROONG_DAWN_REPRINT}
                  orderId={order.id}
                />
              );
          }
        case 'delivery':
          switch (order.agencyType) {
            case 'doobal':
              return (
                <PrintShippingLabelButton
                  type={
                    PRINT_SHIPPING_LABEL_BUTTON_TYPE.DOOBAL_HERO_DELIVERY_REPRINT
                  }
                  orderId={order.id}
                />
              );
            default:
              return <></>;
          }
        default:
          return <></>;
      }
    };

    return (
      <ProgressStatus>
        <PickupCheck>
          <Row>
            <TextComponent fontSize={16}>
              기사님이 조제약을 가져가셨다면 수거 완료를 눌러주세요.
            </TextComponent>
          </Row>
          <div>
            <span data-for="pickupComplete" data-tip>
              <Button
                background={theme.DF_COLOR.WHITE}
                borderColor={theme.DF_COLOR.GREEN500}
                disabled={
                  !(checkList.guide && checkList.pickup) ||
                  isUnavailablePickupComplete
                }
                onClick={() => onSubmitPickupComplete()}>
                <ButtonText color={theme.DF_COLOR.GREEN500}>수거 완료</ButtonText>
              </Button>
            </span>
            {isUnavailablePickupComplete && (
              <Tooltip
                id="pickupComplete"
                place="bottom"
                type="info"
                effect="solid">
                <TextComponent color={theme.DF_COLOR.WHITE}>
                  7시 이후에 기사님이 약을 가져가실 예정입니다.
                </TextComponent>
              </Tooltip>
            )}
          </div>
        </PickupCheck>
        <CheckList>
          <CheckItem onClick={onPressCheck('guide')}>
            {checkList.guide ? <Checked /> : <Unchecked />}
            <CheckTextRow>
              <CheckItemText color={theme.DF_COLOR.PRIMARY500}>[필수]&nbsp;</CheckItemText>
              <CheckItemText color={theme.DF_COLOR.BLACK}>
                환자분께 복약지도를 완료하셨나요?
              </CheckItemText>
            </CheckTextRow>
          </CheckItem>
          <CheckItem onClick={onPressCheck('pickup')}>
            {checkList.pickup ? <Checked /> : <Unchecked />}
            <CheckTextRow>
              <CheckItemText color={theme.DF_COLOR.PRIMARY500}>[필수]&nbsp;</CheckItemText>
              <CheckItemText color={theme.DF_COLOR.BLACK}>
                기사님이 조제약을 가져가셨나요?
              </CheckItemText>
            </CheckTextRow>
          </CheckItem>
        </CheckList>
        {ReprintButton()}
      </ProgressStatus>
    );
  };

  // 수거 완료
  const renderCompletePickup = () => (
    <CompletePickup>
      <Row>
        <StatusTitle>수거 완료</StatusTitle>
        <Division />
        <TextComponent fontSize={16}>
          기사님이 조제약을 가져가셨습니다.
        </TextComponent>
      </Row>
      {order.orderType === 'dawn' && (
        <PrintShippingLabelButton
          type={PRINT_SHIPPING_LABEL_BUTTON_TYPE.VROONG_DAWN_ADD_SHIPPING}
          orderId={order.id}
        />
      )}
    </CompletePickup>
  );

  // 취소 완료
  const renderCancelPickup = () => (
    <Row>
      <StatusTitle>취소 완료</StatusTitle>
      <Division />
      <TextComponent fontSize={16}>취소된 조제 내역입니다.</TextComponent>
    </Row>
  );

  // TODO: 픽업 상태 수정 필요
  // 픽업 수령 완료
  const renderPartnershipPickup = () => (
    <>
      <PickupNotice>
        환자가 조제약을 가져가셨다면 수령 완료를 눌러주세요.
      </PickupNotice>
      <CheckList>
        <CheckItem onClick={onPressCheck('guide')}>
          {checkList.guide ? <Checked /> : <Unchecked />}
          <CheckTextRow>
            <CheckItemText color={theme.DF_COLOR.PRIMARY500}>[필수]&nbsp;</CheckItemText>
            <CheckItemText color={theme.DF_COLOR.BLACK}>
              환자분께 복약지도를 완료하셨나요?
            </CheckItemText>
          </CheckTextRow>
        </CheckItem>
        <CheckItem onClick={onPressCheck('pickup')}>
          {checkList.pickup ? <Checked /> : <Unchecked />}
          <CheckTextRow>
            <CheckItemText color={theme.DF_COLOR.PRIMARY500}>[필수]&nbsp;</CheckItemText>
            <CheckItemText color={theme.DF_COLOR.BLACK}>
              환자분이 조제약을 가져가셨나요?
            </CheckItemText>
          </CheckTextRow>
        </CheckItem>
      </CheckList>
      <Button
        style={{ width: '100%', marginTop: 10 }}
        background={theme.DF_COLOR.GREEN500}
        borderColor={theme.DF_COLOR.GREEN500}
        disabled={!checkList.pickup || !checkList.guide}
        onClick={() => onSubmitPartnershipPickupComplete()}>
        <ButtonText color={theme.DF_COLOR.WHITE}>수령 완료</ButtonText>
      </Button>
    </>
  );

  // 수거 완료
  const renderCompletePartnershipPickup = () => (
    <CompletePickup>
      <Row>
        <StatusTitle>수령 완료</StatusTitle>
        <Division />
        <TextComponent fontSize={16}>
          환자 분께서 약을 수령했습니다.
        </TextComponent>
      </Row>
    </CompletePickup>
  );

  const render = () => {
    switch (orderStatus) {
      case OrderStatusType.Ready:
        return (
          // 조제 승인/거부
          <OrderReady order={order} />
        );
      case OrderStatusType.WaitingPayment: // 가격 요청 후 결제 대기
      case OrderStatusType.RedirectPreparing: // 가격요청 탭에서 결제완료된건 조제진행으로 redirect
        return (
          // 조제약 결제 대기중
          <OrderWaitingPayment order={order} />
        );
      case OrderStatusType.RequestedCancel:
        return renderCancelByUser();
      case OrderStatusType.RequestPickup:
        return renderRequestPickup();
      case OrderStatusType.PickupCheck:
        return renderPickupCheck();
      case OrderStatusType.PickupComplete:
        if (order.agencyType === 'partnership_pickup') {
          return renderCompletePartnershipPickup();
        }
        return renderCompletePickup();
      case OrderStatusType.PickupCancel:
      case OrderStatusType.Cancel:
        return renderCancelPickup();
      case OrderStatusType.PartnershipPickupCheck:
        return renderPartnershipPickup();
      case OrderStatusType.PartnershipPickupRequest:
        return (
          <PartnershipPickup
            onClickResetOrder={() => setOrderResetModal(true)}
            onSubmitPickupCall={onSubmitPickupCall}
          />
        );
      case OrderStatusType.ImmediateOrder:
        return <ImmediateOrder order={order} />;
    }
  };

  return (
    <>
      <Wrapper>{render()}</Wrapper>

      {/* 조제 접수 초기화 모달 */}
      <OrderResetModal clickOverlayToClose={false}>
        <OrderResetForm
          orderId={order.id}
          onClickClose={() => setOrderResetModal(false)}
        />
      </OrderResetModal>
    </>
  );
};

export default BottomButton;

const Wrapper = styled.div`
  flex: 1;
`;

const ReadyStatus = styled.div`
  display: flex;
  flex-flow: row;
  width: 100%;
`;

const Button = styled.button<{ background: string; borderColor: string }>`
  display: flex;
  background: ${({ background }) => background};
  border: ${({ borderColor }) => `1px solid ${borderColor}`};
  padding: 14px 35px;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  margin-right: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }

  :disabled {
    opacity: 0.5;
  }
`;

const ButtonText = styled(TextComponent)<{ color: string }>`
  font-size: 17px;
  font-weight: 500;
  color: ${({ color }) => color};
`;

const ProgressStatus = styled.div``;

const SpaceRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CheckList = styled.div`
  display: inline-flex;
  flex-flow: column;
`;

const CheckItem = styled.div`
  display: inline-flex;
  align-items: center;
  margin-bottom: 14px;
  cursor: pointer;
`;

const CheckTextRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
  margin-left: 8px;
`;
const CheckItemText = styled(TextComponent)<{ color: string }>`
  color: ${({ color }) => color};
  font-size: 16px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const RequestCancel = styled(SpaceRow)`
  margin-bottom: 1.5rem;
`;

const StatusTitle = styled(TextComponent)`
  color: ${theme.DF_COLOR.GREEN500};
  font-size: 24px;
  font-weight: 700;
`;

const Division = styled.div`
  width: 2px;
  height: 20px;
  margin: 0 25px 0 13px;
  background: ${theme.DF_COLOR.GRAY600};
`;

const PickupCheck = styled(SpaceRow)`
  margin-bottom: 1rem;
`;

const CompletePickup = styled(SpaceRow)``;

const PrintShippingLabelButtonWrapper = styled(SpaceRow)`
  flex: 0.65;
  button {
    margin-right: 0;
    padding: 14px 40px;
    width: 100%;
    justify-content: center;
  }
`;

const PickupNotice = styled.div`
  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI16R};
  `};
  margin-bottom: 24px;
`;

const RequestPickup = styled(SpaceRow)``;
